import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "backdrop"]
  static values = {
    collapsed: Boolean
  }

  connect() {
    // Load collapsed state from localStorage
    const savedState = localStorage.getItem("playground-sidebar-collapsed")
    if (savedState !== null) {
      this.collapsedValue = savedState === "true"
    }

    // Wait for menu component to be ready
    this.waitForMenuAndApply()

    // Check if mobile on load
    this.handleResize()
    window.addEventListener("resize", () => this.handleResize())
  }

  disconnect() {
    if (this.menuRegistrationHandler) {
      window.removeEventListener("hakumi-component:registered", this.menuRegistrationHandler)
    }
  }

  waitForMenuAndApply() {
    const menu = document.getElementById("playground-nav-menu")

    // On mobile, always expand (collapsed doesn't make sense in overlay mode)
    if (this.isMobile && this.collapsedValue) {
      this.collapsedValue = false
    }

    // If menu API is already available, apply state immediately
    if (menu?.hakumiComponent?.api) {
      this.applyCollapsedState()
      return
    }

    // Otherwise, wait for the menu to register
    this.menuRegistrationHandler = (event) => {
      if (event.detail?.id === "playground-nav-menu") {
        this.applyCollapsedState()
        window.removeEventListener("hakumi-component:registered", this.menuRegistrationHandler)
      }
    }
    window.addEventListener("hakumi-component:registered", this.menuRegistrationHandler)

    // Also apply CSS class immediately (even if menu API isn't ready yet)
    this.applyCssState()
  }

  toggleCollapse() {
    this.collapsedValue = !this.collapsedValue
    localStorage.setItem("playground-sidebar-collapsed", this.collapsedValue)
    this.applyCollapsedState()
  }

  applyCssState() {
    if (!this.hasSidebarTarget) return

    const icon = this.sidebarTarget.querySelector(".playground-sidebar__collapse-btn .hakumi-icon use")

    if (this.collapsedValue) {
      this.sidebarTarget.classList.add("playground-sidebar--collapsed")
      if (icon) {
        icon.setAttribute("xlink:href", "#icon-menu_unfold")
      }
    } else {
      this.sidebarTarget.classList.remove("playground-sidebar--collapsed")
      if (icon) {
        icon.setAttribute("xlink:href", "#icon-menu_fold")
      }
    }
  }

  applyCollapsedState() {
    const menu = document.getElementById("playground-nav-menu")

    // Apply CSS state
    this.applyCssState()

    // Apply menu API state
    if (menu?.hakumiComponent?.api) {
      if (this.collapsedValue) {
        menu.hakumiComponent.api.collapse()
      } else {
        menu.hakumiComponent.api.expand()
      }
    }
  }

  // Mobile-specific methods
  get isMobile() {
    return window.innerWidth <= 768
  }

  handleResize() {
    if (this.isMobile) {
      // On mobile, always expand menu (collapsed doesn't make sense in overlay)
      if (this.collapsedValue) {
        this.collapsedValue = false
        this.applyCollapsedState()
      }
      // Close mobile menu when resizing
      this.closeMobile()
    }
  }

  toggleMobile() {
    if (this.hasSidebarTarget && this.hasBackdropTarget) {
      // Ensure menu is expanded before opening on mobile
      if (!this.sidebarTarget.classList.contains("playground-sidebar--mobile-open") && this.collapsedValue) {
        this.collapsedValue = false
        this.applyCollapsedState()
      }

      this.sidebarTarget.classList.toggle("playground-sidebar--mobile-open")
      this.backdropTarget.classList.toggle("playground-layout__backdrop--visible")

      // Prevent body scroll when menu is open
      if (this.sidebarTarget.classList.contains("playground-sidebar--mobile-open")) {
        document.body.style.overflow = "hidden"
      } else {
        document.body.style.overflow = ""
      }
    }
  }

  closeMobile() {
    if (this.hasSidebarTarget && this.hasBackdropTarget) {
      this.sidebarTarget.classList.remove("playground-sidebar--mobile-open")
      this.backdropTarget.classList.remove("playground-layout__backdrop--visible")
      document.body.style.overflow = ""
    }
  }

  handleMenuClick(event) {
    // Close mobile menu when clicking on a link (not submenu title)
    if (this.isMobile && event.target.closest(".hakumi-menu-item-link")) {
      // Small delay to allow navigation to start
      setTimeout(() => this.closeMobile(), 100)
    }
  }

  // Touch gesture handlers for swipe-to-open
  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    this.touchStartTime = Date.now()
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return

    const currentX = event.touches[0].clientX
    const currentY = event.touches[0].clientY
    const diffX = currentX - this.touchStartX
    const diffY = currentY - this.touchStartY

    // Only handle horizontal swipes (more horizontal than vertical)
    if (Math.abs(diffX) > Math.abs(diffY) && diffX > 0) {
      // Prevent scrolling while swiping
      event.preventDefault()
    }
  }

  handleTouchEnd(event) {
    if (!this.touchStartX) return

    const touchEndX = event.changedTouches[0].clientX
    const touchEndY = event.changedTouches[0].clientY
    const diffX = touchEndX - this.touchStartX
    const diffY = touchEndY - this.touchStartY
    const diffTime = Date.now() - this.touchStartTime

    // Swipe right at least 50px within 300ms
    // And more horizontal than vertical movement
    if (diffX > 50 && diffTime < 300 && Math.abs(diffX) > Math.abs(diffY) && this.isMobile) {
      this.toggleMobile()
    }

    // Reset touch tracking
    this.touchStartX = null
    this.touchStartY = null
    this.touchStartTime = null
  }
}
